import React from 'react';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Trash2,
  GripVertical,
  Calculator,
  Paperclip,
  ArrowLeftRight,
  FlaskConical
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EditableCell from './EditableCell';

// Individual product table row component
const ProductTableRow = ({
  product,
  index,
  currentColumns,
  readOnly,
  editingCell,
  isEditing,
  onCellEdit,
  onCellValueChange,
  onCellKeyPress,
  onCellSave,
  onPriceLookup,
  onReplace,
  onDelete,
  onProductIdClick,
  calculateColumnValue,
  isCalculatedColumn,
  taxRate,
  hasAttachments = false,
  onAttachmentClick,
  isSample = false
}) => {
  const id = product.id || product._id || `item-${index}`;

  // Always call useSortable hook (React rules requirement)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: readOnly });

  // Only apply drag styles when not in readonly mode
  const style = readOnly ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handlePriceLookupClick = () => {
    if (onPriceLookup) {
      onPriceLookup(index, product);
    }
  };

  const handleReplaceClick = () => {
    if (onReplace) {
      onReplace(index, product);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(product._id || product.id, index);
    }
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      key={product.id || product._id || index}
      className={`
        hover:bg-muted/50
        ${isDragging ? 'cursor-grabbing bg-muted' : 'cursor-default'}
      `}
    >
      <td className="p-3 border-b">
        <div className="flex items-center gap-1">
          {!readOnly && (
            <GripVertical
              {...attributes}
              {...listeners}
              className={`
                mr-2 h-5 w-5
                ${isDragging ? 'text-primary' : 'text-muted-foreground'}
                ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}
                hover:text-primary
              `}
            />
          )}
          <span>{index + 1}</span>
          {isSample && (
            <FlaskConical className="h-4 w-4 text-purple-500" title="样品" />
          )}
        </div>
      </td>
      {currentColumns.map(columnId => (
        <td key={columnId} className="p-3 border-b">
          <EditableCell
            value={product[columnId]}
            fieldId={columnId}
            product={product}
            rowIndex={index}
            isEditing={isEditing(index, columnId)}
            editingCell={editingCell}
            readOnly={readOnly}
            onCellEdit={onCellEdit}
            onCellValueChange={onCellValueChange}
            onCellKeyPress={onCellKeyPress}
            onCellSave={onCellSave}
            onProductIdClick={onProductIdClick}
            calculateColumnValue={calculateColumnValue}
            isCalculatedColumn={isCalculatedColumn}
            taxRate={taxRate}
          />
        </td>
      ))}
      {!readOnly && (
        <td className="p-3 border-b text-center">
          <div className="flex gap-1 justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePriceLookupClick}
                    className="h-8 w-8 text-primary"
                  >
                    <Calculator className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>价格计算</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {onReplace && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleReplaceClick}
                      className="h-8 w-8 text-primary"
                    >
                      <ArrowLeftRight className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>替换产品</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {hasAttachments && onAttachmentClick && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onAttachmentClick(product, index)}
                      className="h-8 w-8 text-secondary-foreground"
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>查看附件</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDeleteClick}
                    className="h-8 w-8 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>删除</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </td>
      )}
    </tr>
  );
};

export default ProductTableRow;
